import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCheck,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { authFetch } from "../utils/api";
import {
  setRole,
  setCurrentOrgId,
  getCurrentOrgId,
  clearWorkspaceScopedSelections,
} from "../utils/permissions";
import "./OrgSwitcher.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Compact workspace switcher pinned to the top of the sidebar.
//
// Selection-only: clicking a workspace switches to it. All editing
// (rename, delete, invite, members) lives on the Workspaces page,
// reachable via the "Manage Workspaces" link at the bottom of the
// dropdown.
const OrgSwitcher = ({ variant } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [currentOrgId, setLocalCurrentOrgId] = useState(getCurrentOrgId());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}orgs`);
      if (!res || !res.ok) return;
      const data = await res.json();
      setOrgs(data.organizations || []);
      if (data.current_org_id) {
        setLocalCurrentOrgId(data.current_org_id);
        setCurrentOrgId(data.current_org_id);
      }
    } catch (e) {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setError(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleSwitch = async (orgId) => {
    if (busy) return;
    if (orgId === currentOrgId) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}orgs/switch`, {
        method: "POST",
        body: JSON.stringify({ organization_id: orgId }),
      });
      if (!res || !res.ok) {
        const data = res ? await res.json().catch(() => ({})) : {};
        throw new Error(data.detail || t("org_switch_failed"));
      }
      const data = await res.json();
      localStorage.setItem("token", data.token);
      setRole(data.role, data.owner_user_id);
      setCurrentOrgId(data.current_org_id);
      // The new workspace has its own set of bots; the previously
      // selected bot id almost certainly doesn't exist here and would
      // cause 403s on Analytics/Inbox/Leads.
      clearWorkspaceScopedSelections();
      // Hard reload so every page re-issues its bot / chat / settings
      // queries against the new workspace, but keep the user on the
      // same page (and same query string / tab) they were on.
      window.location.reload();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const handleManage = () => {
    setOpen(false);
    navigate("/workspaces");
  };

  // Both the label and the chevron just toggle the dropdown — the
  // trigger is selection-only and never navigates anywhere.
  const toggleOpen = () => setOpen((v) => !v);

  const current = orgs.find((o) => o.id === currentOrgId);
  const currentName =
    current?.name ||
    t("org_default_workspace_name", {
      name: localStorage.getItem("first_name") || t("org_default_workspace_owner"),
    });

  return (
    <div className={`org-switcher${variant === "header" ? " header" : ""}`} ref={ref}>
      <div className={`org-switcher-trigger${open ? " open" : ""}`}>
        <button
          type="button"
          className="org-switcher-label"
          onClick={toggleOpen}
          title={currentName}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="org-switcher-icon">
            <FontAwesomeIcon icon={faBuilding} />
          </span>
          <span className="org-switcher-text">
            <span className="org-switcher-role">{t("org_current_workspace_label")}</span>
            <span className="org-switcher-name">{currentName}</span>
          </span>
        </button>
        <button
          type="button"
          className="org-switcher-chev-btn"
          onClick={toggleOpen}
          title={t("org_switch_workspace")}
          aria-label={t("org_switch_workspace")}
        >
          <ExpandMoreIcon
            className="org-switcher-chev"
            sx={{
              transition: 'transform 0.25s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
      </div>

      {open && (
        <div className="org-switcher-menu" role="listbox">
          <div className="org-switcher-menu-header">{t("org_workspaces_header")}</div>

          {orgs.length === 0 && (
            <div className="org-switcher-empty">{t("loading")}</div>
          )}

          {orgs.map((o) => {
            const isCurrent = o.id === currentOrgId;
            return (
              <button
                key={o.id}
                type="button"
                className={`org-switcher-item ${isCurrent ? "current" : ""}`}
                role="option"
                aria-selected={isCurrent}
                onClick={() => handleSwitch(o.id)}
                disabled={busy}
              >
                <span className="org-switcher-item-check">
                  {isCurrent && <FontAwesomeIcon icon={faCheck} />}
                </span>
                <span className="org-switcher-item-text">
                  <span className="org-switcher-item-name">
                    {o.name}
                  </span>
                  <span className={`org-switcher-item-role role-${o.role}`}>
                    {t(`role_${o.role}`, o.role)}
                  </span>
                </span>
              </button>
            );
          })}

          <div className="org-switcher-divider" />

          <button
            type="button"
            className="org-switcher-item manage"
            onClick={handleManage}
            disabled={busy}
          >
            <span className="org-switcher-item-check">
              <FontAwesomeIcon icon={faGear} />
            </span>
            <span className="org-switcher-item-text">
              <span className="org-switcher-item-name">{t("org_manage_workspaces")}</span>
            </span>
          </button>

          {error && <div className="org-switcher-error">{error}</div>}
        </div>
      )}
    </div>
  );
};

export default OrgSwitcher;
